import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getCourse } from '../../api/client';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import LoadingScreen from '../../components/layout/LoadingScreen';
import type { Course, CourseModule, Faq, Testimonial } from '../../types/course';
import {
  Clock,
  Users,
  Award,
  BookOpen,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Play,
  Star,
  Calendar,
  GraduationCap,
} from 'lucide-react';

function safeArray<T>(value: any): T[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function Accordion({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-semibold text-slate-900 text-sm">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CourseDetailScreen(): React.JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'curriculum' | 'faq'>('overview');

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await getCourse(slug);
        setCourse(res.course);
      } catch (error: any) {
        toast.show(error.message || 'Failed to load course', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const handleEnroll = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/enroll/${course?.slug}` } });
      return;
    }
    navigate('/enroll/' + course?.slug, { state: { course } });
  };

  if (loading) return <LoadingScreen message="Loading course..." />;

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Course not found</h2>
          <p className="text-slate-500 mb-4">The course you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/')} leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Back to Courses
          </Button>
        </div>
      </div>
    );
  }

  const benefits = safeArray<string>(course.benefits);
  const skills = safeArray<string>(course.skills_covered);
  const modules = safeArray<CourseModule>(course.modules);
  const faq = safeArray<Faq>(course.faq);
  const testimonials = safeArray<Testimonial>(course.testimonials);

  const amount = (course.discount_price ?? 0) > 0 ? course.discount_price : course.price;
  const hasDiscount = (course.discount_price ?? 0) > 0 && course.discount_price < course.price;

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: BookOpen },
    { id: 'curriculum' as const, label: 'Curriculum', icon: GraduationCap },
    { id: 'faq' as const, label: 'FAQ', icon: Award },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 animate-fade-in">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to courses
      </button>

      {/* Hero section */}
      <div className="grid lg:grid-cols-5 gap-6 mb-8">
        {/* Main content */}
        <div className="lg:col-span-3">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {course.banner_image || course.course_image ? (
              <div className="rounded-2xl overflow-hidden mb-6 shadow-lg">
                <img
                  src={course.banner_image || course.course_image}
                  alt={course.title}
                  className="w-full h-64 object-cover"
                />
              </div>
            ) : (
              <div className="w-full h-64 rounded-2xl bg-amber-500 flex items-center justify-center mb-6">
                <BookOpen className="w-16 h-16 text-white/50" />
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-3">
              {course.category_name && <Badge variant="info">{course.category_name}</Badge>}
              {course.is_featured && <Badge variant="purple">Featured</Badge>}
              {course.is_enrolled && <Badge variant="success">Enrolled</Badge>}
            </div>

            <h1 className="text-3xl font-bold text-slate-900 mb-3 leading-tight">{course.title}</h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-4">
              {course.trainer_name && (
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  {course.trainer_name}
                </span>
              )}
              {course.duration && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {course.duration}
                </span>
              )}
              {course.enrollment_count > 0 && (
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  {course.enrollment_count.toLocaleString()} students
                </span>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-6">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex-1 justify-center ${
                      activeTab === tab.id
                        ? 'bg-white text-amber-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <AnimatePresence mode="wait">
              {activeTab === 'overview' && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-900 mb-3">About this course</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">
                      {course.full_description || course.short_description}
                    </p>
                  </div>

                  {benefits.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                      <h3 className="text-lg font-bold text-slate-900 mb-4">What you'll learn</h3>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {skills.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                      <h3 className="text-lg font-bold text-slate-900 mb-3">Skills covered</h3>
                      <div className="flex flex-wrap gap-2">
                        {skills.map((skill, idx) => (
                          <Badge key={idx} variant="default">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'curriculum' && (
                <motion.div
                  key="curriculum"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-3"
                >
                  {modules.length > 0 ? (
                    modules.map((module, idx) => (
                      <Accordion key={module.id} title={`Module ${idx + 1}: ${module.title}`}>
                        {module.description && (
                          <p className="text-sm text-slate-600 mb-3">{module.description}</p>
                        )}
                        {module.is_free && (
                          <Badge variant="success" size="sm">Free Preview</Badge>
                        )}
                      </Accordion>
                    ))
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Curriculum details coming soon</p>
                    </div>
                  )}
                </motion.div>
              )}

              {activeTab === 'faq' && (
                <motion.div
                  key="faq"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-3"
                >
                  {faq.length > 0 ? (
                    faq.map((item) => (
                      <Accordion key={item.id} title={item.question}>
                        <p className="text-sm text-slate-600">{item.answer}</p>
                      </Accordion>
                    ))
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Award className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No FAQs available yet</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:sticky lg:top-24"
          >
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-lg">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-3xl font-bold text-slate-900">
                  {amount > 0 ? `₹${amount}` : 'Free'}
                </span>
                {hasDiscount && (
                  <span className="text-lg text-slate-400 line-through">₹{course.price}</span>
                )}
              </div>
              {hasDiscount && (
                <Badge variant="success" className="mb-4">
                  Save ₹{course.price - course.discount_price}
                </Badge>
              )}

              {course.is_enrolled ? (
                <Button disabled className="w-full mb-4" variant="secondary">
                  <CheckCircle className="w-4 h-4" />
                  Already Enrolled
                </Button>
              ) : (
                <Button onClick={handleEnroll} className="w-full mb-4" size="lg">
                  {amount > 0 ? 'Enroll Now' : 'Enroll Free'}
                </Button>
              )}

              <p className="text-xs text-slate-400 text-center mb-6">
                30-day money-back guarantee
              </p>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-slate-600">
                  <Play className="w-4 h-4 text-slate-400" />
                  <span>{modules.length || 0} modules</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span>{course.duration || 'Self-paced'}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <Award className="w-4 h-4 text-slate-400" />
                  <span>Certificate of completion</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>Lifetime access</span>
                </div>
              </div>
            </div>

            {/* Testimonials preview */}
            {testimonials.length > 0 && (
              <div className="mt-6 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h3 className="text-sm font-bold text-slate-900 mb-4">Student Reviews</h3>
                <div className="space-y-4">
                  {testimonials.slice(0, 2).map((t) => (
                    <div key={t.id} className="flex gap-3">
                      {t.image ? (
                        <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">
                          {t.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                        <div className="flex gap-0.5 mb-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`w-3 h-3 ${i < t.stars ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-2">{t.quote}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
